/*
  theme.js - the one theme system for this app (TB-064).

  The user picks a mode: 'system' (follow the device), 'light' or 'dark'.
  The choice is stored in localStorage under `tb-theme` and the resolved theme
  is written to <html data-theme="light|dark">, which switches every --tb-*
  token in _tokens.scss. public/index.html applies the stored choice before
  the app loads so there is no flash; this module takes over after that and
  follows OS changes live while the mode is 'system'.

  Widgets that cannot read CSS variables (charts, maps, naive-ui) read
  `resolvedTheme` and re-render when it changes.
*/
import { ref } from 'vue'

export const THEME_KEY = 'tb-theme'
export const THEME_MODES = ['system', 'light', 'dark']
const DEFAULT_MODE = 'system'

const media = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-color-scheme: dark)')
  : null

function readStored () {
  try {
    const v = localStorage.getItem(THEME_KEY)
    return THEME_MODES.includes(v) ? v : DEFAULT_MODE
  } catch (e) {
    return DEFAULT_MODE
  }
}

function resolve (mode) {
  if (mode === 'light' || mode === 'dark') return mode
  return media && media.matches ? 'dark' : 'light'
}

export const themeMode = ref(readStored())
export const resolvedTheme = ref(resolve(themeMode.value))

function apply () {
  resolvedTheme.value = resolve(themeMode.value)
  const root = document.documentElement
  root.setAttribute('data-theme', resolvedTheme.value)
  root.setAttribute('data-theme-mode', themeMode.value)
}

export function setThemeMode (mode) {
  if (!THEME_MODES.includes(mode)) return
  themeMode.value = mode
  try { localStorage.setItem(THEME_KEY, mode) } catch (e) { /* storage blocked: keep for this tab */ }
  apply()
}

// Logout calls localStorage.clear() in several places; the theme is a device
// preference, not session data, so it survives that.
function keepThemeOnClear () {
  try {
    const proto = window.Storage && window.Storage.prototype
    if (!proto || proto.__tbKeepsTheme) return
    const clear = proto.clear
    proto.clear = function () {
      const keep = this === window.localStorage ? this.getItem(THEME_KEY) : null
      clear.call(this)
      if (keep) this.setItem(THEME_KEY, keep)
    }
    proto.__tbKeepsTheme = true
  } catch (e) { /* storage unavailable */ }
}

let started = false
export function initTheme () {
  if (started) return
  started = true
  keepThemeOnClear()
  apply()
  if (media) {
    const onChange = () => { if (themeMode.value === 'system') apply() }
    if (media.addEventListener) media.addEventListener('change', onChange)
    else if (media.addListener) media.addListener(onChange)
  }
  // Keep tabs in sync when the choice changes in another tab.
  window.addEventListener('storage', (e) => {
    if (e.key === THEME_KEY) { themeMode.value = readStored(); apply() }
  })
}

export function useTheme () {
  return { themeMode, resolvedTheme, setThemeMode, modes: THEME_MODES }
}
